import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  conversationsExportProgressPercent,
  downloadConversationsExportText,
  exportWorkspaceConversations,
  type ConversationsExportProgress,
} from "@/lib/conversations-export";

interface WorkspaceConversationsExportProps {
  workspaceId: string;
  workspaceName?: string;
}

export function WorkspaceConversationsExport({
  workspaceId,
  workspaceName,
}: WorkspaceConversationsExportProps) {
  const { toast } = useToast();
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState<ConversationsExportProgress | null>(null);

  const handleExport = async () => {
    setExporting(true);
    setProgress({ phase: "conversations", completed: 0, total: 0 });

    try {
      const exportData = await exportWorkspaceConversations(
        supabase,
        workspaceId,
        setProgress,
      );

      downloadConversationsExportText(exportData, workspaceName);

      toast({
        title: "Exportação concluída",
        description: `${exportData.stats.total_conversations} conversas e ${exportData.stats.total_messages} mensagens exportadas.`,
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Erro desconhecido";
      toast({
        title: "Erro ao exportar conversas",
        description: message,
        variant: "destructive",
      });
    } finally {
      setExporting(false);
      setProgress(null);
    }
  };

  const progressLabel = (() => {
    if (!progress) return null;
    if (progress.phase === "finalizing") return "Finalizando arquivo...";
    if (progress.total <= 0) return "Preparando exportação...";
    const pct = conversationsExportProgressPercent(progress);
    const completed = Math.min(progress.completed, progress.total);
    if (progress.phase === "messages") {
      return `Exportando mensagens: ${completed} de ${progress.total} conversas (${pct}%)`;
    }
    return `Carregando conversas: ${completed} de ${progress.total} (${pct}%)`;
  })();

  const progressPercent =
    progress && progress.total > 0
      ? conversationsExportProgressPercent(progress)
      : exporting
        ? 5
        : 0;

  return (
    <div className="space-y-3 pt-2 border-t">
      <div>
        <p className="text-sm font-medium">Exportar conversas</p>
        <p className="text-xs text-muted-foreground mt-1">
          Baixe todas as conversas do workspace em texto legível (.txt), organizadas por canal, com mensagens em ordem cronológica.
        </p>
      </div>

      {exporting && progress && (
        <div className="space-y-2">
          <Progress value={progressPercent} className="h-1.5" />
          <p className="text-xs text-muted-foreground">{progressLabel}</p>
        </div>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        disabled={exporting}
        className="gap-2"
      >
        {exporting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Download className="w-4 h-4" />
        )}
        {exporting ? "Exportando..." : "Exportar todas as conversas"}
      </Button>
    </div>
  );
}
